/** BOXX Print Bridge browser client, mirroring the shared TS client; keep the two in sync. */
const DEFAULT_URL = 'ws://localhost:8767/print';
const bridgeUrl = () =>
  (typeof window !== 'undefined' && window.__BOXX_PRINT_BRIDGE_URL__) || DEFAULT_URL;

let _seq = 0;
const newReqId = () => `r${Date.now().toString(36)}_${(++_seq).toString(36)}`;

const conn = (() => {
  let ws = null;
  let opening = null;
  const pending = new Map();

  function open() {
    if (ws && ws.readyState === WebSocket.OPEN) return Promise.resolve(ws);
    if (opening) return opening;
    opening = new Promise((resolve, reject) => {
      let settled = false;
      let sock;
      try { sock = new WebSocket(bridgeUrl()); }
      catch (e) { opening = null; reject(e); return; }
      const t = setTimeout(() => {
        if (settled) return;
        settled = true; opening = null;
        try { sock.close(); } catch (_) {}
        reject(new Error('bridge connect timeout'));
      }, 1500);
      sock.onopen = () => { if (settled) return; settled = true; clearTimeout(t); ws = sock; opening = null; resolve(sock); };
      sock.onerror = () => { if (settled) return; settled = true; clearTimeout(t); opening = null; reject(new Error('bridge unavailable')); };
      sock.onclose = () => { if (ws === sock) ws = null; for (const [, p] of pending) { clearTimeout(p.timer); p.reject(new Error('closed')); } pending.clear(); };
      sock.onmessage = (ev) => {
        let msg; try { msg = JSON.parse(ev.data); } catch (_) { return; }
        const p = msg && msg.reqId && pending.get(msg.reqId);
        if (!p) return;
        pending.delete(msg.reqId); clearTimeout(p.timer); p.resolve(msg);
      };
    });
    return opening;
  }

  async function request(cmd, payload, timeoutMs) {
    const sock = await open();
    const reqId = newReqId();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => { pending.delete(reqId); reject(new Error(`${cmd} timed out`)); }, timeoutMs || 20000);
      pending.set(reqId, { resolve, reject, timer });
      try { sock.send(JSON.stringify({ cmd, reqId, ...payload })); }
      catch (e) { pending.delete(reqId); clearTimeout(timer); reject(e); }
    });
  }
  return { request };
})();

// Electron transport when inside a BOXX desktop wrapper, else the loopback agent.
function electronBridge() {
  const b = typeof window !== 'undefined' && window.boxxPrintBridge;
  return b && typeof b.print === 'function' ? b : null;
}
export function isElectronShell() { return electronBridge() !== null; }

let _detect = null;
export async function detectBridge(force) {
  if (electronBridge()) return true;
  if (!force && _detect && Date.now() - _detect.at < 15000) return _detect.ok;
  let ok = false;
  try { ok = (await conn.request('hello', {}, 1800)).event === 'ready'; } catch (_) { ok = false; }
  _detect = { at: Date.now(), ok };
  return ok;
}

export async function listPrinters() {
  const eb = electronBridge();
  if (eb && eb.listPrinters) { try { return await eb.listPrinters(); } catch (_) {} }
  const r = await conn.request('listPrinters', {}, 6000);
  return { printers: Array.isArray(r.printers) ? r.printers : [], default: r.default || '' };
}

async function doPrint(payload) {
  const eb = electronBridge();
  if (eb) {
    try { const r = await eb.print(payload); return r && r.ok ? { ok: true, pages: r.pages } : { ok: false, error: (r && r.error) || 'print failed' }; }
    catch (e) { return { ok: false, error: (e && e.message) || 'electron print failed' }; }
  }
  const r = await conn.request('print', payload, 60000);
  return r && r.event === 'printed' && r.ok ? { ok: true, pages: r.pages } : { ok: false, error: (r && r.message) || 'print failed' };
}
export const printHtml = (html, printerName, geo = {}) => doPrint({ type: 'html', printerName, data: html, ...geo });
export const printPdfBase64 = (b64, printerName, geo = {}) => doPrint({ type: 'pdf', printerName, data: b64, ...geo });

function remembered(rememberKey) {
  if (!rememberKey) return '';
  try { return localStorage.getItem(`boxx.printBridge.printer.${rememberKey}`) || ''; } catch (_) { return ''; }
}
async function resolvePrinter(printerName, rememberKey) {
  let t = (printerName || remembered(rememberKey) || '').trim();
  if (!t) { try { t = (await listPrinters()).default || ''; } catch (_) {} }
  return t;
}

function captureDocumentHtml(target) {
  let css = '';
  for (const sheet of Array.from(document.styleSheets)) {
    try { const rules = sheet.cssRules; if (rules) for (const r of Array.from(rules)) css += r.cssText + '\n'; }
    catch (_) { /* cross-origin */ }
  }
  const head = Array.from(document.head.querySelectorAll('link[rel="stylesheet"], style')).map((n) => n.outerHTML).join('\n');
  const body = (target || document.body).outerHTML;
  return `<!doctype html><html><head><meta charset="utf-8"><base href="${window.location.origin}/">${head}<style>${css}</style></head><body>${body}</body></html>`;
}

/** Direct-print the live page via the bridge; falls back to window.print(). */
export async function printPageOrFallback(opts = {}) {
  const { rememberKey, printerName, target, ...geo } = opts;
  let available = false;
  try { available = await detectBridge(); } catch (_) {}
  if (!available) { window.print(); return 'window'; }
  const printer = await resolvePrinter(printerName, rememberKey);
  if (!printer) { window.print(); return 'window'; }
  try { const r = await printHtml(captureDocumentHtml(target), printer, geo); if (r.ok) return 'bridge'; } catch (_) {}
  window.print(); return 'window';
}

/** UNIVERSAL hook, called once at app entry: overrides window.print() and falls back to the native dialog. */
let _installed = false;
export function installPrintBridge(opts = {}) {
  if (_installed || typeof window === 'undefined') return;
  _installed = true;
  const native = window.print.bind(window);
  // Do NOT eagerly probe the agent: a warm-up connect logs a red WebSocket error on every page without one.
  window.print = function patchedPrint() {
    let html = '';
    try { html = captureDocumentHtml(); } catch (_) { native(); return; }
    (async () => {
      try {
        if (await detectBridge()) {
          const printer = await resolvePrinter(undefined, opts.rememberKey);
          if (printer) { const r = await printHtml(html, printer, opts.geometry || {}); if (r.ok) return; }
        }
      } catch (_) { /* fall through */ }
      native();
    })();
  };
}

export function bridgeInstallUrl() {
  return (typeof window !== 'undefined' && window.__BOXX_PRINT_BRIDGE_INSTALL_URL__) || 'https://saas-boxx.com/downloads/print-bridge/';
}
